import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Whitepaper/resource hero — faithful rewrite of legacy molecules/WhitePaperHero
// (Strapi sections.whitepaper-hero). Gradient band, two columns: left-aligned
// title + description, and a decorative logo mark on the right. Used by
// case-studies, data-sheets, whitepapers, key-integrations, compliance.
export const whitepaperHeroSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  imageUrl: z.string().optional(),
});

type WhitepaperHeroProps = z.infer<typeof whitepaperHeroSchema>;

const Band = styled.section`
  overflow: hidden;
  background: linear-gradient(93deg, #e8efff 0%, #ffffff 100%);
  border-radius: 0 0 8rem 0;
  padding: 0 2.4rem 4.8rem;
`;

const Inner = styled.div`
  max-width: 112rem;
  margin: 0 auto;
  padding-top: 8rem;
  display: grid;
  gap: 3rem;
  align-items: center;

  @media (min-width: 62em) {
    grid-template-columns: 1fr 1fr;
    padding-top: 12rem;
    padding-bottom: 4rem;
  }
`;

const Title = styled.h1`
  margin: 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 3.4rem;
  letter-spacing: -3px;
  line-height: 1.05;

  @media (min-width: 48em) {
    font-size: 7.2rem;
  }
`;

const Description = styled.p`
  margin: 2rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  line-height: 1.6;

  @media (min-width: 48em) {
    font-size: 2rem;
  }
`;

const Image = styled.img`
  display: block;
  width: 20rem;
  height: 20rem;
  margin-left: auto;
  object-fit: contain;

  @media (min-width: 62em) {
    width: 30rem;
    height: 30rem;
  }
`;

function WhitepaperHero({ title, description, imageUrl }: WhitepaperHeroProps) {
  return (
    <Band>
      <Inner>
        <div>
          <Title>{title}</Title>
          {description ? <Description>{description}</Description> : null}
        </div>
        {imageUrl ? <Image src={imageUrl} alt="" /> : null}
      </Inner>
    </Band>
  );
}

export const whitepaperHeroBlock: BlockDefinition<WhitepaperHeroProps> = {
  type: 'whitepaperHero',
  schema: whitepaperHeroSchema,
  Renderer: WhitepaperHero,
};
